import React from "react";
import { Button, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";

export const ButtonBasic = ({
	children,
	size = "medium",
	widthType = "auto",
	width,
	leftSection,
	rightSection,
	actionType = "primary",
	radius = "small",
	loading = false,
	disabled = false,
	onClick,
	...props
}) => {
	const theme = useTheme();

	// Size mapping
	const getSizeStyles = (size) => {
		switch (size) {
			case "xs":
				return { padding: theme.spacing(0.5, 1), fontSize: "0.75rem" };
			case "sm":
				return { padding: theme.spacing(0.75, 1.5), fontSize: "0.875rem" };
			case "md":
				return { padding: theme.spacing(1, 2), fontSize: "1rem" };
			case "lg":
				return { padding: theme.spacing(1.25, 2.5), fontSize: "1.125rem" };
			case "xl":
				return { padding: theme.spacing(1.5, 3), fontSize: "1.25rem" };
			default:
				return { padding: theme.spacing(1, 2), fontSize: "1rem" };
		}
	};

	// Width mapping
	const getWidthStyles = (widthType, width) => {
		switch (widthType) {
			case "full":
				return { width: "100%" };
			case "manual":
				return { width: width };
			case "auto":
			default:
				return { width: "auto" };
		}
	};

	// Radius mapping
	const getRadiusStyles = (radius) => {
		switch (radius) {
			case "none":
				return { borderRadius: 0 };
			case "small":
				return { borderRadius: theme.spacing(1) };
			case "full":
				return { borderRadius: "9999px" };
			default:
				return { borderRadius: theme.spacing(1) };
		}
	};

	// Action type mapping
	const getActionStyles = (actionType) => {
		switch (actionType) {
			case "primary":
				return {
					backgroundColor: theme.customTokens.colors.custom.ui.dark.bold,
					color: theme.palette.common.white,
					"&:hover": {
						backgroundColor: theme.customTokens.colors.gray[8],
					},
					"&:disabled": {
						opacity: 0.5,
					},
				};
			case "secondary":
				return {
					backgroundColor: "transparent",
					color: theme.palette.text.primary,
					border: `1px solid ${theme.palette.grey[300]}`,
					"&:hover": {
						backgroundColor: theme.palette.grey[50],
					},
					"&:disabled": {
						opacity: 0.5,
					},
				};
			default:
				return {
					backgroundColor: theme.customTokens.colors.custom.ui.dark.bold,
					color: theme.palette.common.white,
					"&:hover": {
						backgroundColor: theme.customTokens.colors.gray[8],
					},
					"&:disabled": {
						opacity: 0.5,
					},
				};
		}
	};

	const sizeStyles = getSizeStyles(size);
	const widthStyles = getWidthStyles(widthType, width);
	const radiusStyles = getRadiusStyles(radius);
	const actionStyles = getActionStyles(actionType);

	return (
		<Button
			onClick={onClick}
			disabled={disabled || loading}
			variant="contained"
			sx={{
				...sizeStyles,
				...widthStyles,
				...radiusStyles,
				...actionStyles,
				textTransform: "none",
				fontWeight: 600,
				boxShadow: "none",
				"&:hover": {
					boxShadow: "none",
					...actionStyles["&:hover"],
				},
			}}
			startIcon={
				loading ? (
					<CircularProgress 
						size={16} 
						sx={{ color: actionType === "primary" ? theme.palette.common.white : theme.palette.text.primary }} 
					/>
				) : leftSection
			}
			endIcon={rightSection}
			{...props}
		>
			{children}
		</Button>
	);
};

ButtonBasic.propTypes = {
	children: PropTypes.node.isRequired,
	// xs, sm, md, lg, xl
	size: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl"]),
	// full, auto, manual
	widthType: PropTypes.oneOf(["full", "auto", "manual"]),
	// px, rem, em, %
	width: PropTypes.string,
	// Icon Node
	leftSection: PropTypes.node,
	rightSection: PropTypes.node,
	// primary, secondary
	actionType: PropTypes.oneOf(["primary", "secondary"]),
	// none, small, full
	radius: PropTypes.oneOf(["none", "small", "full"]),
	// Loading state
	loading: PropTypes.bool,
	disabled: PropTypes.bool,
	onClick: PropTypes.func,
};
